import React, { useEffect, useRef, useState } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { trackEvent } from '../../lib/tracking';

const RyadPayment = () => {
  const { t } = useLanguage();
  const sectionRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const handleWhatsAppFinancing = () => {
    trackEvent('InitiateCheckout', {
      content_name: 'Reservation VEFA - Payment CTA',
      content_category: 'Financement',
    });
    const message = encodeURIComponent("Bonjour, je souhaite réserver un appartement à la Résidence RYAD en VEFA.");
    window.open(`https://wa.me/221787172828?text=${message}`, '_blank');
  };

  const steps = [
    { icon: '🏠', gradient: 'from-blue-500 to-blue-600', shadow: 'shadow-blue-500/30' },
    { icon: '🏗️', gradient: 'from-amber-500 to-amber-600', shadow: 'shadow-amber-500/30' },
    { icon: '🗝️', gradient: 'from-green-500 to-green-600', shadow: 'shadow-green-500/30' },
  ];

  const advantageIcons = ['💰', '🛡️', '💳', '📈'];
  const featureIcons = ['📋', '⚡', '🤝'];

  return (
    <section id="vefa" ref={sectionRef} className="py-24 bg-gradient-to-b from-gray-50 to-white relative overflow-hidden">
      {/* Background decorations */}
      <div className="absolute inset-0">
        <div className="absolute top-0 left-0 w-full h-32 bg-gradient-to-b from-slate-900 to-transparent"></div>
        <div className="absolute top-40 right-0 w-96 h-96 bg-amber-500/5 rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 left-0 w-80 h-80 bg-blue-500/5 rounded-full blur-3xl"></div>
      </div>

      <div className="container mx-auto px-4 relative z-10">
        {/* Section Header */}
        <div className={`text-center mb-20 transform transition-all duration-1000 ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'}`}>
          <span className="inline-block px-4 py-2 bg-amber-100 text-amber-700 rounded-full text-sm font-semibold mb-6 tracking-wide uppercase">
            Paiement sécurisé
          </span>
          <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-6">
            <span className="gradient-text">{t.vefa.title}</span>
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            {t.vefa.subtitle}
          </p>
        </div>

        <div className="max-w-6xl mx-auto">
          {/* VEFA Advantages - Modern Grid */}
          <div className={`mb-16 transform transition-all duration-1000 delay-400 ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'}`}>
            <div className="bg-white rounded-3xl p-8 md:p-12 shadow-xl border border-gray-100">
              <h3 className="text-2xl md:text-3xl font-bold text-gray-900 text-center mb-10">
                {t.vefa.why_title}
              </h3>
              
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
                {t.vefa.advantages.map((advantage, index) => (
                  <div 
                    key={index} 
                    className="group text-center p-6 rounded-2xl bg-gradient-to-br from-gray-50 to-white hover:from-amber-50 hover:to-white transition-all duration-300 border border-transparent hover:border-amber-200 card-hover"
                  >
                    <div className="w-16 h-16 bg-gradient-to-br from-amber-100 to-amber-200 rounded-2xl flex items-center justify-center text-3xl mb-4 mx-auto transform group-hover:scale-110 group-hover:-rotate-6 transition-all duration-300">
                      {advantageIcons[index]}
                    </div>
                    <h4 className="font-bold text-gray-900 mb-2 group-hover:text-amber-600 transition-colors">
                      {advantage.title}
                    </h4>
                    <p className="text-gray-500 text-sm">{advantage.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Banking Section - Premium Design */}
          <div className={`transform transition-all duration-1000 delay-600 ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'}`}>
            <div className="relative overflow-hidden rounded-3xl">
              {/* Background */}
              <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-gray-900 to-slate-800"></div>
              <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxwYXRoIGQ9Ik0zNiAxOGMtOS45NDEgMC0xOCA4LjA1OS0xOCAxOHM4LjA1OSAxOCAxOCAxOCAxOC04LjA1OSAxOC0xOC04LjA1OS0xOC0xOC0xOHoiIHN0cm9rZT0icmdiYSgyNTUsMjU1LDI1NSwwLjA1KSIvPjwvZz48L3N2Zz4=')] opacity-50"></div>
              
              {/* Decorative blurs */}
              <div className="absolute top-0 right-0 w-64 h-64 bg-amber-500/10 rounded-full blur-3xl"></div>
              <div className="absolute bottom-0 left-0 w-48 h-48 bg-blue-500/10 rounded-full blur-3xl"></div>
              
              <div className="relative p-8 md:p-12 text-center">
                {/* Icon */}
                <div className="w-20 h-20 bg-gradient-to-br from-amber-400 to-amber-600 rounded-2xl flex items-center justify-center text-4xl mb-8 mx-auto shadow-xl shadow-amber-500/30">
                  🏦
                </div>
                
                <h3 className="text-2xl md:text-3xl font-bold text-white mb-4">
                  {t.vefa.financing.title}
                </h3>
                <p className="text-lg mb-10 max-w-2xl mx-auto text-gray-400">
                  {t.vefa.financing.subtitle}
                </p>
                
                {/* Features */}
                <div className="grid md:grid-cols-3 gap-6 max-w-3xl mx-auto mb-10">
                  {t.vefa.financing.features.map((feature, index) => (
                    <div 
                      key={index} 
                      className="group bg-white/5 backdrop-blur-sm rounded-2xl p-6 border border-white/10 hover:border-amber-500/30 hover:bg-white/10 transition-all duration-300"
                    >
                      <div className="w-12 h-12 bg-amber-500/20 rounded-xl flex items-center justify-center text-2xl mb-4 mx-auto group-hover:scale-110 transition-transform">
                        {featureIcons[index]}
                      </div>
                      <p className="font-semibold text-white">{feature}</p>
                    </div>
                  ))}
                </div>

                {/* CTA Button */}
                <button
                  onClick={handleWhatsAppFinancing}
                  className="group inline-flex items-center bg-gradient-to-r from-green-600 to-green-500 hover:from-green-500 hover:to-green-400 text-white px-10 py-5 rounded-2xl font-bold text-lg transition-all duration-500 transform hover:scale-105 hover:-translate-y-1 shadow-xl shadow-green-500/30"
                >
                  <svg className="w-6 h-6 mr-3 group-hover:scale-110 transition-transform" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.488"/>
                  </svg>
                  {t.vefa.financing.cta}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default RyadPayment;
